import { of, EMPTY, Subject, merge } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { fetchJson } from './http.js';
import { renderTemplate, replaceViewContainer, showError } from './ui.js';

/**
 * View factory for the main flashcard study page.
 *
 * Fetches a random card from /api/cards/random on load and each time the
 * user clicks "Nächste Karte". The reveal button toggles the card back.
 * A 404 response (no cards for this user) is shown as a silent status
 * message rather than a generic error, so controls remain usable.
 *
 * Returns an Observable whose lifetime equals the view's lifetime.
 * Unsubscribing (on route change) automatically tears down all event
 * listeners and cancels in-flight operations.
 */
export function createMainView() {
    return of(undefined).pipe(
        switchMap(() => {
            let root = renderTemplate('main-view');
            replaceViewContainer(root);

            let cardFront = root.querySelector('.card-front');
            let cardBack = root.querySelector('.card-back');
            let revealBtn = root.querySelector('.reveal-btn');
            let nextBtn = root.querySelector('.next-btn');
            let statusEl = root.querySelector('.status');

            let nextCard$ = new Subject();

            function resetReveal() {
                cardBack.hidden = true;
                revealBtn.textContent = 'Antwort zeigen';
            }

            function showCard(card) {
                cardFront.textContent = card.front;
                cardBack.textContent = card.back;
                statusEl.textContent = '';
                resetReveal();
            }

            function showEmptyDeck() {
                cardFront.textContent = '';
                cardBack.textContent = '';
                statusEl.textContent = 'Keine Karten vorhanden.';
                resetReveal();
            }

            revealBtn.addEventListener('click', () => {
                cardBack.hidden = !cardBack.hidden;
                revealBtn.textContent = cardBack.hidden
                    ? 'Antwort zeigen'
                    : 'Antwort verbergen';
            });

            nextBtn.addEventListener('click', () => nextCard$.next());

            let card$ = merge(of(undefined), nextCard$).pipe(
                switchMap(() => {
                    statusEl.textContent = 'Laden…';
                    return fetchJson('/api/cards/random').pipe(
                        tap((card) => showCard(card)),
                        catchError((err) => {
                            if (err instanceof Response && err.status === 404) {
                                showEmptyDeck();
                                return EMPTY;
                            }
                            return (showError(err), EMPTY);
                        })
                    );
                })
            );

            return card$;
        }),
        catchError((err) => {
            showError(err);
            return EMPTY;
        })
    );
}
